import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, isManagerRole } from "@/context/AuthContext";
import { WerkveldProvider } from "@/context/WerkveldContext";
import { MenuIndelingProvider } from "@/context/MenuIndelingContext";
import { LayoutV2Provider } from "@/hooks/useLayoutV2";
import { LayoutV3Provider } from "@/hooks/useLayoutV3";
import { OfflineSync } from "@/components/OfflineSync";

// ---- Eager imports (nodig direct bij eerste load / auth-flow) ----
import Login from "@/pages/Login";
import WachtwoordVergeten from "@/pages/WachtwoordVergeten";
import ResetWachtwoord from "@/pages/ResetWachtwoord";
import VerifyCertificaat from "@/pages/VerifyCertificaat";
import KlachtenPortaal from "@/pages/KlachtenPortaal";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";

import { heeftRol } from "@/lib/rollen";
// ---- Lazy-loaded routes (code-split bundle per pagina) ----
const Medewerkers = lazy(() => import("@/pages/Medewerkers"));
const MedewerkerDetail = lazy(() => import("@/pages/MedewerkerDetail"));
const OndertekenVerlenging = lazy(() => import("@/pages/OndertekenVerlenging"));
const Verlof = lazy(() => import("@/pages/Verlof"));
const Roosterkalender = lazy(() => import("@/pages/Roosterkalender"));
const Urenregels = lazy(() => import("@/pages/Urenregels"));
const Assets = lazy(() => import("@/pages/Assets"));
const AssetScan = lazy(() => import("@/pages/AssetScan"));
const Vergaderingen = lazy(() => import("@/pages/Vergaderingen"));
const Toolboxen = lazy(() => import("@/pages/Toolboxen"));
const Bijeenkomsten = lazy(() => import("@/pages/Bijeenkomsten"));
const Normschemas = lazy(() => import("@/pages/Normschemas"));
const VergaderingenPlus = lazy(() => import("@/pages/VergaderingenPlus"));
const SeoTool = lazy(() => import("@/pages/SeoTool"));
const KlachtVisie = lazy(() => import("@/pages/KlachtVisie"));
const DeelLink = lazy(() => import("@/pages/DeelLink"));
const Overlegnotities = lazy(() => import("@/pages/Overlegnotities"));
const Automations = lazy(() => import("@/pages/Automations"));
const Documenten = lazy(() => import("@/pages/Documenten"));
const BbDocumenten = lazy(() => import("@/pages/BbDocumenten"));
const Takenpakketten = lazy(() => import("@/pages/Takenpakketten"));
const Leenauto = lazy(() => import("@/pages/Leenauto"));
const Activiteiten = lazy(() => import("@/pages/Activiteiten"));
const Financials = lazy(() => import("@/pages/Financials"));
const MijnGegevens = lazy(() => import("@/pages/MijnGegevens"));
const AIAssistent = lazy(() => import("@/pages/AIAssistent"));
const AIRapportages = lazy(() => import("@/pages/AIRapportages"));
const BulkInvite = lazy(() => import("@/pages/BulkInvite"));
const Tickets = lazy(() => import("@/pages/Tickets"));
const Klachten = lazy(() => import("@/pages/Klachten"));
const KlachtenNaadloos = lazy(() => import("@/pages/KlachtenNaadloos"));
const Locaties = lazy(() => import("@/pages/Locaties"));
const Snelkoppelingen = lazy(() => import("@/pages/Snelkoppelingen"));
const Bekwaamheden = lazy(() => import("@/pages/Bekwaamheden"));
const Groepen = lazy(() => import("@/pages/Groepen"));
const Bestellingen = lazy(() => import("@/pages/Bestellingen"));
const VoorraadTeller = lazy(() => import("@/pages/VoorraadTeller"));
const CRM = lazy(() => import("@/pages/CRM"));
const Klantdossier = lazy(() => import("@/pages/Klantdossier"));
const Zaken = lazy(() => import("@/pages/Zaken"));
const Relaties = lazy(() => import("@/pages/Relaties"));
const Instellingen = lazy(() => import("@/pages/Instellingen"));
const Beslissingen = lazy(() => import("@/pages/Beslissingen"));
const Meldingen = lazy(() => import("@/pages/Meldingen"));
const Beoordelen = lazy(() => import("@/pages/Beoordelen"));
const CRMKoppelingen = lazy(() => import("@/pages/CRMKoppelingen"));
const Facturatie = lazy(() => import("@/pages/Facturatie"));
const Crediteuren = lazy(() => import("@/pages/Crediteuren"));
const CrediteurenInbox = lazy(() => import("@/pages/CrediteurenInbox"));
const Werkbonnen = lazy(() => import("@/pages/Werkbonnen"));
const Onderhoudscertificaten = lazy(() => import("@/pages/Onderhoudscertificaten"));
const Preventiecertificaten = lazy(() => import("@/pages/Preventiecertificaten"));
const ReobRapporten = lazy(() => import("@/pages/ReobRapporten"));
const WerkbusOverdracht = lazy(() => import("@/pages/WerkbusOverdracht"));
const Planbord = lazy(() => import("@/pages/Planbord"));
const AuditLog = lazy(() => import("@/pages/AuditLog"));
const Kalender = lazy(() => import("@/pages/Kalender"));
const AgendaDashboard = lazy(() => import("@/pages/AgendaDashboard"));
const Ontruimingsplan = lazy(() => import("@/pages/Ontruimingsplan"));
const Verbeteringen = lazy(() => import("@/pages/Verbeteringen"));
const Projecten = lazy(() => import("@/pages/Projecten"));
const BrandveiligheidProjecten = lazy(() => import("@/pages/BrandveiligheidProjecten"));
const Contracten = lazy(() => import("@/pages/Contracten"));
const ContractTekenen = lazy(() => import("@/pages/ContractTekenen"));
const OntruimingsplanDeel = lazy(() => import("@/pages/OntruimingsplanDeel"));
const MedewerkerIntake = lazy(() => import("@/pages/MedewerkerIntake"));
const OnderaannemerSetup = lazy(() => import("@/pages/OnderaannemerSetup"));
const OnderaannemerPortaal = lazy(() => import("@/pages/OnderaannemerPortaal"));
const PartnerLogin = lazy(() => import("@/pages/PartnerLogin"));
const Meekijk = lazy(() => import("@/pages/Meekijk"));
const Partners = lazy(() => import("@/pages/Partners"));
const MenuVoorbeelden = lazy(() => import("@/pages/MenuVoorbeelden"));
const DomeinStart = lazy(() => import("@/pages/DomeinStart"));
const AlleTools = lazy(() => import("@/pages/AlleTools"));
const BBMaterialen = lazy(() => import("@/pages/BBMaterialen"));
const OvereenkomstPubliek = lazy(() => import("@/pages/OvereenkomstPubliek"));
const IdUploadPubliek = lazy(() => import("@/pages/IdUploadPubliek"));
const CrediteurBetalingen = lazy(() => import("@/pages/CrediteurBetalingen"));
const Leads = lazy(() => import("@/pages/Leads"));
const BMILeads = lazy(() => import("@/pages/BMILeads"));
const Commissie = lazy(() => import("@/pages/Commissie"));
const Archief = lazy(() => import("@/pages/Archief"));
const Datakwaliteit = lazy(() => import("@/pages/Datakwaliteit"));
const AIInstructies = lazy(() => import("@/pages/AIInstructies"));
const AdminBeheer = lazy(() => import("@/pages/AdminBeheer"));
const Systeemcontrole = lazy(() => import("@/pages/Systeemcontrole"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const BBKlanten = lazy(() => import("@/pages/BBKlanten"));
const BBPipeline = lazy(() => import("@/pages/BBPipeline"));
const VasteKlanten = lazy(() => import("@/pages/VasteKlanten"));
const ZohoVergelijking = lazy(() => import("@/pages/ZohoVergelijking"));
const Cyclus2025 = lazy(() => import("@/pages/Cyclus2025"));
const InplanRapport = lazy(() => import("@/pages/InplanRapport"));
const RondleidingBeheer = lazy(() => import("@/pages/RondleidingBeheer"));
const VasteKlantKaart = lazy(() => import("@/pages/VasteKlantKaart"));
const UitvalPlanner = lazy(() => import("@/pages/UitvalPlanner"));
const OutSmartSync = lazy(() => import("@/pages/OutSmartSync"));
const InkomendeBetalingen = lazy(() => import("@/pages/InkomendeBetalingenUnified"));
const ContanteBetalingen = lazy(() => import("@/pages/ContanteBetalingen"));
const MailTemplates = lazy(() => import("@/pages/MailTemplates"));
const HandtekeningBeheer = lazy(() => import("@/pages/HandtekeningBeheer"));
const PrivacyNotities = lazy(() => import("@/pages/PrivacyNotities"));
const MijnPrivacyNotities = lazy(() => import("@/pages/MijnPrivacyNotities"));
const DataMigratie = lazy(() => import("@/pages/DataMigratie"));
const MailDashboard = lazy(() => import("@/pages/MailDashboard"));
const BptInbox = lazy(() => import("@/pages/BptInbox"));
const Beeldstudio = lazy(() => import("@/pages/Beeldstudio"));
const MailVolgsysteem = lazy(() => import("@/pages/MailVolgsysteem"));
const Aanbestedingen = lazy(() => import("@/pages/Aanbestedingen"));
const AanbestedingDetail = lazy(() => import("@/pages/AanbestedingDetail"));
const IctHub = lazy(() => import("@/pages/IctHub"));
const SignatureWizard = lazy(() => import("@/pages/SignatureWizard"));
const ExtrasHub = lazy(() => import("@/pages/ExtrasHub"));
const KoppelingenHub = lazy(() => import("@/pages/KoppelingenHub"));
const DebiteurenKpiDashboard = lazy(() => import("@/pages/DebiteurenKpiDashboard"));
const WhatsappHerinneringen = lazy(() => import("@/pages/WhatsappHerinneringen"));
const Belagent = lazy(() => import("@/pages/Belagent"));
const DebiteurenBeheer = lazy(() => import("@/pages/DebiteurenBeheer"));
const MijnDashboards = lazy(() => import("@/pages/MijnDashboards"));
const AgentControlCenter = lazy(() => import("@/pages/AgentControlCenter"));
const RevolutSettings = lazy(() => import("@/pages/RevolutSettings"));
const MyposSettings = lazy(() => import("@/pages/MyposSettings"));
const MollieOverzicht = lazy(() => import("@/pages/MollieOverzicht"));
const CashFlowDashboard = lazy(() => import("@/pages/CashFlowDashboard"));
const OmzetAnalytics = lazy(() => import("@/pages/OmzetAnalytics"));
const CrediteurWorkflow = lazy(() => import("@/pages/CrediteurWorkflow"));
const BunqBetalingen = lazy(() => import("@/pages/BunqBetalingen"));
const BestellingenTeVerwerken = lazy(() => import("@/pages/BestellingenTeVerwerken"));
const Reminders = lazy(() => import("@/pages/Reminders"));
const Taken = lazy(() => import("@/pages/Taken"));
const Berichten = lazy(() => import("@/pages/Berichten"));
const DashboardsOverview = lazy(() => import("@/pages/DashboardsOverview"));
const MedewerkersRechten = lazy(() => import("@/pages/MedewerkersRechten"));
const Verzendingen = lazy(() => import("@/pages/Verzendingen"));
const CommissieWorkflow = lazy(() => import("@/pages/CommissieWorkflow"));
const CommissiePersoneelModule = lazy(() => import("@/pages/CommissiePersoneelModule"));
const MijnCommissie = lazy(() => import("@/pages/MijnCommissie"));
const SubgroepHub = lazy(() => import("@/pages/SubgroepHub"));
const MijnUren = lazy(() => import("@/pages/MijnUren"));
const MijnDeclaraties = lazy(() => import("@/pages/MijnDeclaraties"));
const MijnReiskosten = lazy(() => import("@/pages/MijnReiskosten"));
const DeclaratiesKeurenPage = lazy(() => import("@/pages/DeclaratiesKeuren"));
const UrenKeuren = lazy(() => import("@/pages/UrenKeuren"));
const BoetesPage = lazy(() => import("@/pages/Boetes"));
const MijnBoetes = lazy(() => import("@/pages/MijnBoetes"));
const PeriodiekeCorrectie = lazy(() => import("@/pages/PeriodiekeCorrectie"));
const PersoneelszakenOverzicht = lazy(() => import("@/pages/PersoneelszakenOverzicht"));
const Postvak = lazy(() => import("@/pages/Postvak"));
const Cursussen = lazy(() => import("@/pages/Cursussen"));
const CursusDetail = lazy(() => import("@/pages/CursusDetail"));
const LiveLocatie = lazy(() => import("@/pages/LiveLocatie"));
const LocatieKaart = lazy(() => import("@/pages/LocatieKaart"));
const SocialMediaPlanner = lazy(() => import("@/pages/SocialMediaPlanner"));
const Handboek = lazy(() => import("@/pages/Handboek"));
const SyncHealth = lazy(() => import("@/pages/SyncHealth"));
const ApiSleutels = lazy(() => import("@/pages/ApiSleutels"));
const ApiDocs = lazy(() => import("@/pages/ApiDocs"));
const McpToestemming = lazy(() => import("@/pages/McpToestemming"));
const Ontwikkelen = lazy(() => import("@/pages/Ontwikkelen"));
const DynModule = lazy(() => import("@/pages/DynModule"));
const AutoBoekRegels = lazy(() => import("@/pages/AutoBoekRegels"));
const KlantPortaalLogin = lazy(() => import("@/pages/KlantPortaal").then(m => ({ default: m.KlantPortaalLogin })));
const KlantPortaalTokenExchange = lazy(() => import("@/pages/KlantPortaal").then(m => ({ default: m.KlantPortaalTokenExchange })));
const KlantPortaalDashboard = lazy(() => import("@/pages/KlantPortaal").then(m => ({ default: m.KlantPortaalDashboard })));
const OntruimingsplanCanvas = lazy(() => import("@/pages/OntruimingsplanCanvas"));
const BBFollowUpDashboard = lazy(() => import("@/pages/BBFollowUpDashboard"));
const BBProjectMails = lazy(() => import("@/pages/BBProjectMails"));
const BBProjectWorkflow = lazy(() => import("@/pages/BBProjectWorkflow"));
const Opleidingscontracten = lazy(() => import("@/pages/Opleidingscontracten"));
const OpleidingscontractOndertekenen = lazy(() => import("@/pages/OpleidingscontractOndertekenen"));

function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center text-slate-400">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-slate-300 border-t-red-600 rounded-full animate-spin" />
        <div className="text-sm">Pagina laden…</div>
      </div>
    </div>
  );
}

// managerOnly-routes die via custom_permissies ("Extra toegang") tóch open mogen
const PATH_MODULE = {
  "/medewerkers": "Medewerkers",
  "/groepen": "Hiërarchie",
  "/crm-koppelingen": "CRM koppelingen",
  "/crediteuren": "Crediteuren",
  "/crediteuren-inbox": "Crediteuren Inbox",
  "/planbord": "Planbord",
  "/audit-log": "Audit log",
  "/contracten": "Onderhoudscontracten",
  "/crediteur-betalingen": "SEPA-betalingen",
  "/commissie": "Commissieberekening",
  "/declaraties-keuren": "Declaraties keuren",
  "/uren-keuren": "Uren keuren",
  "/boetes": "Boetes",
  "/periodieke-correctie": "Periodieke correctie",
  "/personeelszaken-overzicht": "Personeelsoverzicht",
  "/account-beheer": "Accounts & rechten",
  "/beheer/bulk-invite": "Bulk-uitnodiging",
  "/rol-permissies": "Rol-permissies",
  "/rechten": "Rol-permissies",
  "/postvak": "Postvak",
  "/bpt-inbox": "BPT Inbox (AI)",
  "/beeldstudio": "Beeldstudio (AI)",
  "/archief": "Archief (verwijderd)",
  "/datakwaliteit": "Systeembeheer",
  "/ai-instructies": "AI-instructies",
  "/admin-beheer": "Systeembeheer",
  "/systeemcontrole": "Systeembeheer",
  "/opleidingscontracten": "Opleidingscontracten",
  "/outsmart-sync": "OutSmart sync",
  "/inkomende-betalingen": "Inkomende betalingen",
  "/auto-boek-regels": "Auto-boek regels",
  "/mail-templates": "Mail-templates",
  "/handtekeningen": "Handtekeningen",
  "/data-migratie": "Data-migratie",
  "/mail-dashboard": "Mail-dashboard",
  "/mail-volgsysteem": "Mail Volgsysteem",
  "/aanbestedingen": "Aanbestedingen",
  "/ict-hub": "ICT",
  "/extras": "Extras",
  "/koppelingen": "Koppelingen Hub",
  "/externe-toegang": "Koppelingen Hub",
  "/externe-toegang/docs": "Koppelingen Hub",
  "/ontwikkelen": "Koppelingen Hub",
  "/agent-control": "Agent Control",
  "/revolut": "Revolut",
  "/cash-flow": "Cash Flow",
  "/debiteuren-kpi": "Debiteuren KPI",
  "/whatsapp-herinneringen": "WhatsApp-herinneringen",
  "/belagent": "AI-belagent",
  "/debiteurenbeheer": "Debiteurenbeheer",
  "/omzet-analytics": "Omzet analytics",
  "/bunq": "Bunq-betalingen",
  "/mypos": "MyPOS pin-transacties",
  "/mollie": "Mollie",
  "/bestellingen-te-verwerken": "Bestellingen",
  "/dashboards": "Uitgebreide dashboards",
  "/partners": "Onderaannemers BB",
  "/locatie-kaart": "Locatie-kaart",
  "/social-media": "Social Media Planner",
  "/seo-tool": "SEO & Vindbaarheid",
  "/sync-health": "Sync status",
  "/klantdossiers": "Klantdossiers",
  "/zaken": "Zaken",
  "/instellingen": "Instellingen",
  "/beslissingen": "Mijn beslissingen",
  "/meldingen": "Meldingen",
  "/beoordelen": "Voorstellen beoordelen",
  "/relaties": "Relaties",
  "/klantdossier": "Klantdossier",
  "/externe-toegang": "API-sleutels",
  "/externe-toegang/docs": "API-documentatie",
  "/ontwikkelen": "Ontwikkelen",
  "/sync-overzicht": "Sync status",
};

function Protected({ children, managerOnly = false, directorOnly = false, bbOnly = false, kantoorOk = false }) {
  const { user, loading, permissies } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Laden…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "onderaannemer") return <Navigate to="/portaal" replace />;
  if (directorOnly && user.role !== "manager_plus") return <Navigate to="/dashboard" replace />;
  if (bbOnly && !isManagerRole(user) && user.role !== "projectleider_pb") {
    const extra = [...(user.custom_permissies || []), ...((permissies && permissies.modules_lezen) || [])];
    if (user.role === "applicateur" || !(extra.includes("BB Documenten") || extra.includes("Projecten BB"))) return <Navigate to="/dashboard" replace />;
  }
  if (managerOnly && !isManagerRole(user) && !(kantoorOk && heeftRol(user, ["kcc", "salesteam"]))) {
    const pad = location.pathname;
    const moduleLabel = PATH_MODULE[pad] || (pad.startsWith("/medewerkers/") ? "Medewerkers" : null);
    // Toegang via per-gebruiker custom_permissies ÓF via rol-permissies-matrix (modules_lezen)
    const extra = [...(user.custom_permissies || []), ...((permissies && permissies.modules_lezen) || [])];
    if (!(moduleLabel && extra.includes(moduleLabel))) return <Navigate to="/dashboard" replace />;
  }
  return (
    <Layout>
      <Suspense fallback={<PageLoader />}>{children}</Suspense>
    </Layout>
  );
}

function PortaalProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Laden…</div>;
  if (!user) return <Navigate to="/partner-login" replace />;
  if (user.role !== "onderaannemer") return <Navigate to="/dashboard" replace />;
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

function NaLoginRedirect({ user }) {
  const { search } = useLocation();  // router-locatie, niet window.location (race met nav() in Login)
  const n = new URLSearchParams(search).get("next");
  const veilig = n && n.startsWith("/") && !n.startsWith("//") ? n : null;
  return <Navigate to={user.role === "onderaannemer" ? "/portaal" : (veilig || "/dashboard")} replace />;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={loading ? null : user ? <NaLoginRedirect user={user} /> : <Login />} />
      <Route path="/mcp-toestemming" element={<McpToestemming />} />
      {/* PUBLIEK — geen auth voor contract-tekenen door klant */}
      <Route path="/contract-tekenen/:token" element={<ContractTekenen />} />
      {/* PUBLIEK — klant bekijkt/print ontruimingsplan via deellink + pincode */}
      <Route path="/deel/ontruimingsplan/:token" element={<Suspense fallback={<PageLoader />}><OntruimingsplanDeel /></Suspense>} />
      <Route path="/medewerker-intake/:token" element={<MedewerkerIntake />} />
      {/* PUBLIEK — onderaannemer stelt wachtwoord in via beveiligde uitnodigingslink */}
      <Route path="/onderaannemer-setup/:token" element={<Suspense fallback={null}><OnderaannemerSetup /></Suspense>} />
      {/* PARTNER-LOGIN — aparte inlogpagina voor onderaannemers/partners */}
      <Route path="/partner-login" element={<Suspense fallback={null}><PartnerLogin /></Suspense>} />
      {/* ONDERAANNEMER-PORTAAL — eigen afgeschermde omgeving (geen BPT-layout) */}
      <Route path="/portaal" element={<PortaalProtected><OnderaannemerPortaal /></PortaalProtected>} />
      {/* PUBLIEK — medewerker ondertekent contractverlenging via beveiligde link */}
      <Route path="/onderteken/verlenging/:token" element={<OndertekenVerlenging />} />
      <Route path="/wachtwoord-vergeten" element={loading ? null : user ? <Navigate to="/dashboard" replace /> : <WachtwoordVergeten />} />
      <Route path="/reset-wachtwoord/:token" element={<ResetWachtwoord />} />
      {/* PUBLIEKE verificatie-pagina — bereikbaar via QR-code op certificaat, GEEN auth */}
      <Route path="/verify/:certNr" element={<VerifyCertificaat />} />
      {/* PUBLIEK klachten-portaal — GEEN auth, voor externe klanten */}
      <Route path="/klachten-portaal" element={<KlachtenPortaal />} />
      <Route path="/klacht" element={<KlachtenPortaal />} />
      <Route path="/klacht-visie/:token" element={<Suspense fallback={null}><KlachtVisie /></Suspense>} />
      <Route path="/deel/:token" element={<Suspense fallback={null}><DeelLink /></Suspense>} />
      {/* KLANT-PORTAAL — magic-link login voor externe klanten */}
      <Route path="/klant-portaal" element={<KlantPortaalLogin />} />
      <Route path="/klant-portaal/token/:token" element={<KlantPortaalTokenExchange />} />
      <Route path="/klant-portaal/dashboard" element={<KlantPortaalDashboard />} />
      {/* PUBLIEK — studieovereenkomst ondertekenen via magic-link (geen auth) */}
      <Route path="/opleidingscontract/ondertekenen/:token" element={<OpleidingscontractOndertekenen />} />
      {/* PUBLIEK — onderaannemer vult overeenkomst in, uploadt bijlagen en ondertekent via beveiligde link */}
      <Route path="/overeenkomst/:token" element={<OvereenkomstPubliek />} />
      <Route path="/id-upload/:token" element={<IdUploadPubliek />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/ai-assistent" element={<Protected><AIAssistent /></Protected>} />
      <Route path="/ai-rapportages" element={<Protected><AIRapportages /></Protected>} />
      <Route path="/beheer/bulk-invite" element={<Protected><BulkInvite /></Protected>} />
      <Route path="/medewerkers" element={<Protected managerOnly><Medewerkers /></Protected>} />
      <Route path="/medewerkers/:id" element={<Protected managerOnly><MedewerkerDetail /></Protected>} />
      <Route path="/groepen" element={<Protected managerOnly><Groepen /></Protected>} />
      <Route path="/mijn-gegevens" element={<Protected><MijnGegevens /></Protected>} />
      <Route path="/verlof" element={<Protected><Verlof /></Protected>} />
      <Route path="/roosterkalender" element={<Protected><Roosterkalender /></Protected>} />
      <Route path="/urenregels" element={<Protected><Urenregels /></Protected>} />
      <Route path="/assets" element={<Protected><Assets /></Protected>} />
      <Route path="/asset-scan/:id" element={<Protected><AssetScan /></Protected>} />
      <Route path="/bekwaamheden" element={<Protected><Bekwaamheden /></Protected>} />
      <Route path="/takenpakketten" element={<Protected><Takenpakketten /></Protected>} />
      <Route path="/leenauto" element={<Protected><Leenauto /></Protected>} />
      <Route path="/tickets" element={<Protected><Tickets /></Protected>} />
      <Route path="/klachten" element={<Protected><Klachten /></Protected>} />
      <Route path="/klachten/naadloos" element={<Protected><KlachtenNaadloos /></Protected>} />
      <Route path="/locaties" element={<Protected><Locaties /></Protected>} />
      <Route path="/snelkoppelingen" element={<Protected><Snelkoppelingen /></Protected>} />
      <Route path="/vergaderingen" element={<Protected><Vergaderingen /></Protected>} />
      <Route path="/toolboxen" element={<Protected><Toolboxen /></Protected>} />
      <Route path="/bijeenkomsten" element={<Protected><Bijeenkomsten /></Protected>} />
      <Route path="/normschemas" element={<Protected><Normschemas /></Protected>} />
      <Route path="/vergaderingen-plus" element={<Protected><VergaderingenPlus /></Protected>} />
      <Route path="/overlegnotities" element={<Protected><Overlegnotities /></Protected>} />
      <Route path="/automations" element={<Protected><Automations /></Protected>} />
      <Route path="/kalender" element={<Protected><Kalender /></Protected>} />
      <Route path="/agenda-dashboard" element={<Protected><AgendaDashboard /></Protected>} />
      <Route path="/activiteiten" element={<Protected><Activiteiten /></Protected>} />
      <Route path="/ontruimingsplan" element={<Protected><Ontruimingsplan /></Protected>} />
      <Route path="/ontruimingsplan/plattegrond" element={<Protected><OntruimingsplanCanvas /></Protected>} />
      <Route path="/ontruimingsplan/plattegrond/:id" element={<Protected><OntruimingsplanCanvas /></Protected>} />
      <Route path="/verbeteringen" element={<Protected><Verbeteringen /></Protected>} />
      <Route path="/projecten" element={<Protected><Projecten /></Protected>} />
      <Route path="/bv-projecten" element={<Protected><BrandveiligheidProjecten /></Protected>} />
      <Route path="/training" element={<Navigate to="/cursussen" replace />} />
      <Route path="/documenten" element={<Protected><Documenten /></Protected>} />
      <Route path="/bb-documenten" element={<Protected bbOnly><BbDocumenten /></Protected>} />
      <Route path="/bb-materialen" element={<Protected><BBMaterialen /></Protected>} />
      <Route path="/bestellingen" element={<Protected><Bestellingen /></Protected>} />
      <Route path="/voorraad-teller" element={<Protected><VoorraadTeller /></Protected>} />
      <Route path="/crm" element={<Protected><CRM /></Protected>} />
      <Route path="/klantdossiers" element={<Protected managerOnly><Klantdossier /></Protected>} />
      <Route path="/zaken" element={<Protected><Zaken /></Protected>} />
      <Route path="/instellingen" element={<Protected directorOnly><Instellingen /></Protected>} />
      <Route path="/beslissingen" element={<Protected><Beslissingen /></Protected>} />
      <Route path="/meldingen" element={<Protected><Meldingen /></Protected>} />
      <Route path="/beoordelen" element={<Protected managerOnly><Beoordelen /></Protected>} />
      <Route path="/relaties" element={<Protected><Relaties /></Protected>} />
      <Route path="/klantdossier" element={<Navigate to="/klantdossiers" replace />} />
      <Route path="/klantdossier/:klantId" element={<Protected><Klantdossier /></Protected>} />
      <Route path="/crm-koppelingen" element={<Protected managerOnly><CRMKoppelingen /></Protected>} />
      <Route path="/facturatie" element={<Protected><Facturatie /></Protected>} />
      <Route path="/offertes" element={<Protected><Facturatie initialTab="offertes" /></Protected>} />
      <Route path="/facturen" element={<Protected><Facturatie initialTab="facturen" /></Protected>} />
      <Route path="/crediteuren" element={<Protected managerOnly><Crediteuren /></Protected>} />
      <Route path="/crediteuren-inbox" element={<Protected><CrediteurenInbox /></Protected>} />
      <Route path="/werkbonnen" element={<Protected><Werkbonnen /></Protected>} />
      <Route path="/onderhoudscertificaten" element={<Protected><Onderhoudscertificaten /></Protected>} />
      <Route path="/preventiecertificaten" element={<Protected><Preventiecertificaten /></Protected>} />
      <Route path="/reob-rapporten" element={<Protected><ReobRapporten /></Protected>} />
      <Route path="/werkbus-overdracht" element={<Protected><WerkbusOverdracht /></Protected>} />
      <Route path="/planbord" element={<Protected managerOnly><Planbord /></Protected>} />
      <Route path="/audit-log" element={<Protected managerOnly><AuditLog /></Protected>} />
      <Route path="/financials" element={<Protected><Financials /></Protected>} />
      <Route path="/contracten" element={<Protected managerOnly><Contracten /></Protected>} />
      <Route path="/crediteur-betalingen" element={<Protected managerOnly><CrediteurBetalingen /></Protected>} />
      <Route path="/leads" element={<Protected><Leads /></Protected>} />
      <Route path="/bmi-leads" element={<Protected><BMILeads /></Protected>} />
      <Route path="/commissie" element={<Protected managerOnly><CommissiePersoneelModule /></Protected>} />
      <Route path="/account-beheer" element={<Navigate to="/rechten?tab=accounts" replace />} />
      <Route path="/archief" element={<Protected managerOnly><Archief /></Protected>} />
      <Route path="/datakwaliteit" element={<Protected managerOnly><Datakwaliteit /></Protected>} />
      <Route path="/ai-instructies" element={<Protected managerOnly><AIInstructies /></Protected>} />
      <Route path="/admin-beheer" element={<Protected managerOnly><AdminBeheer /></Protected>} />
      <Route path="/systeemcontrole" element={<Protected managerOnly><Systeemcontrole /></Protected>} />
      <Route path="/bb-klanten" element={<Protected><BBKlanten /></Protected>} />
      <Route path="/bb-pipeline" element={<Protected><BBPipeline /></Protected>} />
      <Route path="/bb-followup" element={<Protected><BBFollowUpDashboard /></Protected>} />
      <Route path="/bb-project-mails" element={<Protected><BBProjectMails /></Protected>} />
      <Route path="/bb-workflow" element={<Protected><BBProjectWorkflow /></Protected>} />
      <Route path="/bb-workflow/:id" element={<Protected><BBProjectWorkflow /></Protected>} />
      <Route path="/opleidingscontracten" element={<Protected managerOnly><Opleidingscontracten /></Protected>} />
      <Route path="/vaste-klanten" element={<Protected><VasteKlanten /></Protected>} />
      <Route path="/vaste-klanten/zoho-vergelijking" element={<Protected directorOnly><ZohoVergelijking /></Protected>} />
      <Route path="/vaste-klanten/cyclus-2025" element={<Protected directorOnly><Cyclus2025 /></Protected>} />
      <Route path="/vaste-klanten/inplannen-week" element={<Protected directorOnly><InplanRapport /></Protected>} />
      <Route path="/vaste-klanten/:id" element={<Protected><VasteKlantKaart /></Protected>} />
      <Route path="/uitval-planner" element={<Protected><UitvalPlanner /></Protected>} />
      <Route path="/outsmart-sync" element={<Protected managerOnly><OutSmartSync /></Protected>} />
      <Route path="/inkomende-betalingen" element={<Protected managerOnly kantoorOk><InkomendeBetalingen /></Protected>} />
      <Route path="/contante-betalingen" element={<Protected><ContanteBetalingen /></Protected>} />
      <Route path="/mail-templates" element={<Protected managerOnly><MailTemplates /></Protected>} />
      <Route path="/handtekeningen" element={<Protected managerOnly><HandtekeningBeheer /></Protected>} />
      <Route path="/privacy-notities" element={<Protected directorOnly><PrivacyNotities /></Protected>} />
      <Route path="/mijn-privacy-notities" element={<Protected><MijnPrivacyNotities /></Protected>} />
      <Route path="/data-migratie" element={<Protected managerOnly><DataMigratie /></Protected>} />
      <Route path="/mail-dashboard" element={<Protected managerOnly><MailDashboard /></Protected>} />
      <Route path="/bpt-inbox" element={<Protected managerOnly><BptInbox /></Protected>} />
      <Route path="/beeldstudio" element={<Protected managerOnly><Beeldstudio /></Protected>} />
      <Route path="/mail-volgsysteem" element={<Protected><MailVolgsysteem /></Protected>} />
      <Route path="/aanbestedingen" element={<Protected><Aanbestedingen /></Protected>} />
      <Route path="/aanbestedingen/:aid" element={<Protected><AanbestedingDetail /></Protected>} />
      <Route path="/ict-hub" element={<Protected managerOnly><IctHub /></Protected>} />
      <Route path="/mijn-handtekening" element={<Protected><SignatureWizard /></Protected>} />
      <Route path="/extras" element={<Protected managerOnly><ExtrasHub /></Protected>} />
      <Route path="/koppelingen" element={<Protected managerOnly><KoppelingenHub /></Protected>} />
      <Route path="/debiteuren-kpi" element={<Protected managerOnly><DebiteurenKpiDashboard /></Protected>} />
      <Route path="/whatsapp-herinneringen" element={<Protected managerOnly><WhatsappHerinneringen /></Protected>} />
      <Route path="/belagent" element={<Protected managerOnly><Belagent /></Protected>} />
      <Route path="/debiteurenbeheer" element={<Protected managerOnly><DebiteurenBeheer /></Protected>} />
      <Route path="/mijn-dashboards" element={<Protected><MijnDashboards /></Protected>} />
      <Route path="/agent-control" element={<Protected managerOnly><AgentControlCenter /></Protected>} />
      <Route path="/revolut" element={<Protected managerOnly><RevolutSettings /></Protected>} />
      <Route path="/cash-flow" element={<Protected managerOnly><CashFlowDashboard /></Protected>} />
      <Route path="/omzet-analytics" element={<Protected managerOnly><OmzetAnalytics /></Protected>} />
      <Route path="/crediteur-workflow" element={<Protected><CrediteurWorkflow /></Protected>} />
      <Route path="/bunq" element={<Protected managerOnly><BunqBetalingen /></Protected>} />
      <Route path="/mypos" element={<Protected managerOnly><MyposSettings /></Protected>} />
      <Route path="/mollie" element={<Protected managerOnly><MollieOverzicht /></Protected>} />
      <Route path="/bestellingen-te-verwerken" element={<Protected managerOnly><BestellingenTeVerwerken /></Protected>} />
      <Route path="/reminders" element={<Protected><Reminders /></Protected>} />
      <Route path="/taken" element={<Protected><Taken /></Protected>} />
      <Route path="/berichten" element={<Protected><Berichten /></Protected>} />
      <Route path="/dashboards" element={<Protected managerOnly><DashboardsOverview /></Protected>} />
      <Route path="/rechten" element={<Protected managerOnly><MedewerkersRechten /></Protected>} />
      <Route path="/rol-permissies" element={<Navigate to="/rechten" replace />} />
      <Route path="/verzendingen" element={<Protected><Verzendingen /></Protected>} />
      <Route path="/commissie-workflow" element={<Navigate to="/commissie?tab=workflow" replace />} />
      <Route path="/mijn-commissie" element={<Protected><MijnCommissie /></Protected>} />
      <Route path="/meekijk" element={<Protected><Meekijk /></Protected>} />
      <Route path="/partners" element={<Protected managerOnly><Partners /></Protected>} />
      <Route path="/bb-onderaannemers" element={<Protected bbOnly><Partners bb /></Protected>} />
      <Route path="/menu-voorbeelden" element={<Protected managerOnly><MenuVoorbeelden /></Protected>} />
      <Route path="/start/:domein" element={<Protected><DomeinStart /></Protected>} />
      <Route path="/alle-tools" element={<Protected><AlleTools /></Protected>} />
      <Route path="/hub/:cat/:subgroup" element={<Protected><SubgroepHub /></Protected>} />
      <Route path="/hub/:cat" element={<Protected><SubgroepHub /></Protected>} />
      <Route path="/mijn-uren" element={<Protected><MijnUren /></Protected>} />
      <Route path="/mijn-reiskosten" element={<Protected><MijnReiskosten /></Protected>} />
      <Route path="/declaraties" element={<Protected><MijnDeclaraties /></Protected>} />
      <Route path="/declaraties-keuren" element={<Protected managerOnly><DeclaratiesKeurenPage /></Protected>} />
      <Route path="/uren-keuren" element={<Protected managerOnly><UrenKeuren /></Protected>} />
      <Route path="/boetes" element={<Protected managerOnly><BoetesPage /></Protected>} />
      <Route path="/mijn-boetes" element={<Protected><MijnBoetes /></Protected>} />
      <Route path="/periodieke-correctie" element={<Protected managerOnly><PeriodiekeCorrectie /></Protected>} />
      <Route path="/personeelszaken-overzicht" element={<Protected managerOnly><PersoneelszakenOverzicht /></Protected>} />
      <Route path="/postvak" element={<Protected><Postvak /></Protected>} />
      <Route path="/cursussen" element={<Protected><Cursussen /></Protected>} />
      <Route path="/cursussen/:id" element={<Protected><CursusDetail /></Protected>} />
      <Route path="/live-locatie" element={<Protected><LiveLocatie /></Protected>} />
      <Route path="/locatie-kaart" element={<Protected managerOnly><LocatieKaart /></Protected>} />
      <Route path="/social-media" element={<Protected managerOnly><SocialMediaPlanner /></Protected>} />
      <Route path="/seo-tool" element={<Protected managerOnly><SeoTool /></Protected>} />
      <Route path="/handboek" element={<Protected><Handboek /></Protected>} />
      <Route path="/rondleiding-beheer" element={<Protected directorOnly><RondleidingBeheer /></Protected>} />
      <Route path="/sync-health" element={<Protected managerOnly><SyncHealth /></Protected>} />
      <Route path="/externe-toegang" element={<Protected managerOnly kantoorOk><ApiSleutels /></Protected>} />
      <Route path="/externe-toegang/docs" element={<Protected managerOnly kantoorOk><ApiDocs /></Protected>} />
      <Route path="/ontwikkelen" element={<Protected directorOnly><Ontwikkelen /></Protected>} />
      <Route path="/m/:slug/*" element={<Protected><DynModule /></Protected>} />
      <Route path="/sync-overzicht" element={<Navigate to="/sync-health" replace />} />
      <Route path="/auto-boek-regels" element={<Protected managerOnly><AutoBoekRegels /></Protected>} />
      <Route path="*" element={<Protected><NotFound /></Protected>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
        <WerkveldProvider>
          <LayoutV2Provider>
          <LayoutV3Provider>
          <MenuIndelingProvider>
            <AppRoutes />
            <OfflineSync />
            <Toaster richColors position="top-right" />
          </MenuIndelingProvider>
          </LayoutV3Provider>
          </LayoutV2Provider>
        </WerkveldProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
